/**
 * Shared time-of-day palette + helpers for the Today surface.
 *
 * Extracted (POL-6) from `TodayHeroBackdrop.tsx` so multiple
 * backdrops could read from one source of truth — one gradient
 * recipe per bucket, one bucket picker.
 *
 * POL-10: the boxed hero backdrop was retired and folded into
 * `TodayPageBackdrop.tsx` (single page-wide sky).  The web now
 * has one consumer for these helpers on Today; iOS still uses the
 * same recipe in `TodayHeroBackdrop.swift` (mounted as the
 * ScrollView background, so it's effectively page-wide there too).
 *
 * POL-9: yellow / hot-amber stops in the morning, midday, sunrise
 * and sunset palettes were swapped for softer editorial tones
 * (dusty rose / warm cream / muted rose-gold).  The old palette
 * shipped `#FFB74D`, `#F5DEB3` and `#F1C085` / `#F4A261` — all
 * pushed toward electric amber and read as "juice" against the
 * cosmic UI.  New stops sit in the same warm family but tuned to
 * the editorial palette.  iOS mirror lives in `TodayHeroBackdrop
 * .swift` — keep the two in visual parity.
 */

export type TimeOfDay =
  | 'sunrise'
  | 'morning'
  | 'midday'
  | 'sunset'
  | 'twilight'
  | 'night';

export interface TodayGradient {
  colors: [string, string, string];
  starColor: string;
  starCount: number;
  showMoon: boolean;
}

export function bucketFromHour(hour: number): TimeOfDay {
  if (hour >= 5 && hour < 8) return 'sunrise';
  if (hour >= 8 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 16) return 'midday';
  if (hour >= 16 && hour < 19) return 'sunset';
  if (hour >= 19 && hour < 21) return 'twilight';
  return 'night';
}

export function gradientFor(tod: TimeOfDay): TodayGradient {
  switch (tod) {
    case 'sunrise':
      // POL-9: dropped `#F1C085` (amber-yellow) → `#E9B098` (muted
      // rose-gold). Kept the coral core so sunrise still reads warm.
      // POL-11d: starCount 18 → 10 so page-wide density lands in the
      // 8-10 target for sunrise instead of blanketing the sky.
      return {
        colors: ['#F5A277', '#E76F51', '#E9B098'],
        starColor: 'rgba(255,255,255,0.55)',
        starCount: 10,
        showMoon: false,
      };
    case 'morning':
      // POL-9: `#FFB74D` (hot amber) → `#EDB6A0` (dusty rose).
      // POL-11d: 8 → 4 to hit the 3-5 morning target.
      // GLASS-1: pale sky stops (#87CEEB / #B7D9E8 / #EDB6A0) were
      // washing out white Today content.  Swapped to deeper
      // early-overcast tones so 100% white text stays legible
      // everywhere on the gradient.  iOS mirror: `TodayHeroBackdrop
      // .swift` — keep both in parity.
      return {
        colors: ['#4E7A96', '#5E8AA5', '#8A6E68'],
        starColor: 'rgba(255,255,255,0.4)',
        starCount: 4,
        showMoon: false,
      };
    case 'midday':
      // POL-9: `#F5DEB3` (wheat) → `#F0E6D8` (warm cream / bone).
      // GLASS-1: bright sky stops (#87CEEB / #4A90E2 / #F0E6D8)
      // were the loudest legibility offender.  Swapped to
      // cinematic overcast noon (dusky teal → dark denim → warm
      // taupe horizon) so cards + white text keep contrast at
      // midday.  iOS mirror in `TodayHeroBackdrop.swift`.
      return {
        colors: ['#3E6B8A', '#2E5478', '#8B7D6E'],
        starColor: 'rgba(255,255,255,0.3)',
        starCount: 0,
        showMoon: false,
      };
    case 'sunset':
      // POL-9: trailing `#F4A261` (amber) → `#E88C6E` (rose-gold).
      // POL-11d: 34 → 18 for the 15-20 sunset target.
      return {
        colors: ['#5B2A86', '#E85A9C', '#E88C6E'],
        starColor: 'rgba(255,239,220,0.7)',
        starCount: 18,
        showMoon: false,
      };
    case 'twilight':
      // POL-11d: 70 → 40 for the 30-45 twilight target.
      return {
        colors: ['#3D2B5C', '#5B3A6E', '#8E4162'],
        starColor: 'rgba(255,239,220,0.85)',
        starCount: 40,
        showMoon: false,
      };
    case 'night':
    default:
      // POL-11d: 130 kept — it now multiplies by 1× in
      // TodayPageBackdrop (was 3×), so total ≈130 stars land in the
      // 100-140 night target.
      return {
        colors: ['#0F1729', '#141A34', '#1A0F2E'],
        starColor: 'rgba(255,239,220,0.95)',
        starCount: 130,
        showMoon: true,
      };
  }
}

/**
 * Deep charcoal Today "floor" — the color the page-wide gradient
 * fades to below the fold so cards keep contrast.  Matches the
 * cosmic canvas well without being pitch-black.
 */
export const TODAY_PAGE_FLOOR = '#0A0A0A';

/**
 * CSS `linear-gradient(...)` for the hero-box treatment — three
 * stops, top to bottom.
 */
export function heroLinearGradient(g: TodayGradient): string {
  return `linear-gradient(180deg, ${g.colors[0]} 0%, ${g.colors[1]} 55%, ${g.colors[2]} 100%)`;
}

/**
 * CSS `linear-gradient(...)` for the page-wide treatment — sky at
 * top, fades into `TODAY_PAGE_FLOOR` at the bottom for card
 * readability.  Uses fixed viewport heights so the sky always
 * stays above the fold and long scrolls land on the charcoal
 * floor without a hard seam.
 *
 * POL-11c — tightened the fade curve so the sky belongs to the
 * fold and everything scrolled deep in the page sits on editorial
 * black.  Old curve stretched color 3 out to 80vh and only hit
 * the charcoal floor at 110vh, which kept the sky visible well
 * past the fold and made cards below compete with it.  New curve:
 *   • sky owns the first 25vh (nothing changes above the fold)
 *   • starts fading toward charcoal at 55vh
 *   • fully on `TODAY_PAGE_FLOOR` by 100vh (hard cut)
 * So Inquiries / pipeline / cards below the fold sit on solid
 * editorial black instead of muddying against a lingering sky.
 */
export function pageLinearGradient(g: TodayGradient): string {
  return [
    `linear-gradient(180deg,`,
    `  ${g.colors[0]} 0vh,`,
    `  ${g.colors[1]} 25vh,`,
    `  ${g.colors[2]} 55vh,`,
    `  ${TODAY_PAGE_FLOOR} 100vh,`,
    `  ${TODAY_PAGE_FLOOR} 100%`,
    `)`,
  ].join(' ');
}
